import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Slide {
  src: string;
  alt: string;
  contain?: boolean;
}

const slides: Slide[] = [
  {
    src: '/images/Shomen.jpg',
    alt: 'Shomenuchi techniek op de mat',
    contain: true,
  },
  {
    src: '/images/KyuexamensBanner.jpg',
    alt: 'Kyu examens bij Anshin',
  },
  {
    src: '/images/SankyoSeminar.jpg',
    alt: 'Aikido techniek tijdens seminar',
  },
  {
    src: '/images/Mattenwagen.jpg',
    alt: 'Samen de matten klaarzetten in de dojo',
  },
  {
    src: '/images/ZonMeditatie.jpg',
    alt: 'Meditatie in het ochtendlicht',
  },
  {
    src: '/images/StarWars2.jpg',
    alt: 'Wapenles en stoktechnieken',
  },
];

export default function HeroCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    if (isPaused) return;

    const timer = setInterval(() => {
      nextSlide();
    }, 4000); // Wisselt om de 4 seconden

    return () => clearInterval(timer);
  }, [nextSlide, isPaused]);

  return (
    <div
      className="relative w-full h-64 sm:h-80 md:h-96 lg:h-[440px] rounded-2xl overflow-hidden shadow-xl bg-gray-900 group"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      aria-label="Fotocarrousel Aikido vereniging Anshin"
    >
      {/* Slides met vloeiende fade overgang */}
      {slides.map((slide, index) => {
        const isActive = index === currentIndex;
        return (
          <div
            key={slide.src}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              isActive ? 'opacity-100 z-10' : 'opacity-0 pointer-events-none z-0'
            }`}
          >
            {slide.contain ? (
              <div className="relative w-full h-full flex items-center justify-center overflow-hidden bg-stone-900">
                {/* Onscherpe sfeervolle achtergrond van dezelfde foto */}
                <img
                  src={slide.src}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-40 select-none pointer-events-none"
                />
                {/* Volledige originele foto zonder dat er iets wordt afgesneden */}
                <img
                  src={slide.src}
                  alt={slide.alt}
                  className="relative z-10 h-full w-auto max-w-full object-contain drop-shadow-2xl select-none"
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              </div>
            ) : (
              <img
                src={slide.src}
                alt={slide.alt}
                className="w-full h-full object-cover select-none"
                loading={index === 0 ? 'eager' : 'lazy'}
              />
            )}
            {/* Subtiel verloop voor diepte */}
            <div className="absolute inset-0 bg-linear-to-t from-black/40 via-transparent to-transparent pointer-events-none z-20" />
          </div>
        );
      })}

      {/* Pijl links */}
      <button
        type="button"
        onClick={prevSlide}
        aria-label="Vorige foto"
        className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 z-30 p-2 sm:p-2.5 rounded-full bg-black/40 hover:bg-black/70 text-white backdrop-blur-xs transition-all opacity-0 group-hover:opacity-100 focus:opacity-100"
      >
        <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
      </button>

      {/* Pijl rechts */}
      <button
        type="button"
        onClick={nextSlide}
        aria-label="Volgende foto"
        className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 z-30 p-2 sm:p-2.5 rounded-full bg-black/40 hover:bg-black/70 text-white backdrop-blur-xs transition-all opacity-0 group-hover:opacity-100 focus:opacity-100"
      >
        <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
      </button>

      {/* Indicatie bolletjes / dots */}
      <div className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center space-x-2 bg-black/30 backdrop-blur-xs px-3 py-1.5 rounded-full">
        {slides.map((slide, index) => (
          <button
            key={slide.src}
            type="button"
            onClick={() => setCurrentIndex(index)}
            aria-label={`Ga naar foto ${index + 1}: ${slide.alt}`}
            className={`transition-all duration-300 rounded-full ${
              index === currentIndex
                ? 'w-6 h-2 bg-[#D7790F]'
                : 'w-2 h-2 bg-white/60 hover:bg-white'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
